/**
 * Phone number utilities
 * Hybrid approach: Fast custom helpers for NANP, google-libphonenumber for international
 *
 * NANP (North American Numbering Plan):
 * - US/Canada/Caribbean: +1 (XXX) XXX-XXXX
 *
 * International:
 * - All other countries handled by google-libphonenumber
 *
 * Development Mode:
 * - Uses simple formatting without validation to support test phone numbers
 *
 * Performance:
 * - google-libphonenumber (~200KB) is lazily loaded only when needed
 * - In development mode, the library is not loaded at all
 */

type ParsedPhoneNumber = unknown

type PhoneNumberUtilLike = {
  parse(phone: string, defaultRegion?: string): ParsedPhoneNumber
  isValidNumber(number: ParsedPhoneNumber): boolean
  format(number: ParsedPhoneNumber, format: number): string
  getRegionCodeForNumber(number: ParsedPhoneNumber): string | undefined
}

type PhoneNumberFormatLike = {
  NATIONAL: number
  INTERNATIONAL: number
  E164: number
}

type GoogleLibPhoneNumberLike = {
  PhoneNumberUtil: {
    getInstance(): PhoneNumberUtilLike
  }
  PhoneNumberFormat: PhoneNumberFormatLike
}

// Lazy-loaded phone utility (saves ~200KB from initial bundle)
let phoneUtil: PhoneNumberUtilLike | null = null
let PhoneNumberFormat: PhoneNumberFormatLike | null = null

/**
 * Lazily initialize the phone utility
 * Only loads the library when first production validation is needed
 */
async function getPhoneUtil() {
  if (!phoneUtil) {
    const lib =
      (await import('google-libphonenumber')) as unknown as GoogleLibPhoneNumberLike
    phoneUtil = lib.PhoneNumberUtil.getInstance()
    PhoneNumberFormat = lib.PhoneNumberFormat
  }
  return { phoneUtil, PhoneNumberFormat: PhoneNumberFormat! }
}

/**
 * Synchronous phone util getter (for sync functions)
 * Returns null if library not yet loaded
 */
function getPhoneUtilSync() {
  return phoneUtil ? { phoneUtil, PhoneNumberFormat: PhoneNumberFormat! } : null
}

/**
 * Format phone numbers for development (no validation)
 * Returns (XXX) XXX-XXXX format for any 10-digit number
 * Supports test/mock numbers that may not be valid in production
 * Also used as fallback when library not yet loaded
 */
function formatPhoneNumberSimple(phone: string): string {
  const cleaned = phone.replace(/\D/g, '')
  // Remove leading '1' if present
  const withoutCountryCode = cleaned.startsWith('1')
    ? cleaned.slice(1)
    : cleaned

  if (withoutCountryCode.length === 10) {
    const areaCode = withoutCountryCode.slice(0, 3)
    const prefix = withoutCountryCode.slice(3, 6)
    const lineNumber = withoutCountryCode.slice(6, 10)
    return `(${areaCode}) ${prefix}-${lineNumber}`
  }

  // Fallback to original if format is unexpected
  return phone
}

/**
 * Format phone number for display in +1(xxx)xxx-xxxx format
 * Used for consistent display across the app (tables, cards, exports)
 *
 * @param phone - Phone number in any format (E.164, raw digits, etc.)
 * @returns Formatted phone number in +1(xxx)xxx-xxxx format
 *
 * @example
 * formatPhoneForDisplay('+13709436024') // Returns: +1(370)943-6024
 * formatPhoneForDisplay('4445555450') // Returns: +1(444)555-5450
 */
export function formatPhoneForDisplay(phone: string): string {
  const cleaned = phone.replace(/\D/g, '')
  // Remove leading '1' if present to normalize
  const withoutCountryCode = cleaned.startsWith('1')
    ? cleaned.slice(1)
    : cleaned

  if (withoutCountryCode.length === 10) {
    const areaCode = withoutCountryCode.slice(0, 3)
    const prefix = withoutCountryCode.slice(3, 6)
    const lineNumber = withoutCountryCode.slice(6, 10)
    return `+1(${areaCode})${prefix}-${lineNumber}`
  }

  // Fallback to original if format is unexpected
  return phone
}

/**
 * Preload the phone library (call on app init or before validation)
 * This allows sync functions to work immediately after preload
 */
export async function preloadPhoneLibrary(): Promise<void> {
  if (process.env.NODE_ENV === 'development') return
  await getPhoneUtil()
}

/**
 * Format phone number for display (human-readable)
 * Supports international numbers using google-libphonenumber
 *
 * @param phone - Phone number in E.164 format (e.g., +14445555450, +821012345678)
 * @param defaultRegion - Default region code if not in E.164 format (default: 'US')
 * @returns Formatted phone number in national format or original if invalid
 *
 * @example
 * formatPhoneNumber('+14445555450') // US: (444) 555-5450
 * formatPhoneNumber('+14165551234') // Canada: (416) 555-1234
 * formatPhoneNumber('+821012345678') // Korea: 010-1234-5678
 * formatPhoneNumber('+442071234567') // UK: 020 7123 4567
 * formatPhoneNumber('+33123456789') // France: 01 23 45 67 89
 */
export function formatPhoneNumber(
  phone: string,
  defaultRegion: string = 'US'
): string {
  // In development, use simple formatting without validation
  // This allows test/mock phone numbers to work without errors
  if (process.env.NODE_ENV === 'development') {
    return formatPhoneNumberSimple(phone)
  }

  // Production: Use full validation with google-libphonenumber
  const lib = getPhoneUtilSync()
  if (!lib) {
    // Library not yet loaded, use simple fallback
    return formatPhoneNumberSimple(phone)
  }

  try {
    // Clean the input - remove any whitespace
    const cleaned = phone.trim()

    // Parse the phone number
    const number = lib.phoneUtil.parse(cleaned, defaultRegion)

    // Validate before formatting
    if (!lib.phoneUtil.isValidNumber(number)) {
      // Invalid number - return original
      return phone
    }

    // Format to national format (human-readable for that country)
    return lib.phoneUtil.format(number, lib.PhoneNumberFormat.NATIONAL)
  } catch {
    // If parsing fails, return original
    return phone
  }
}

/**
 * Format phone number to international format
 * Returns international format with spaces (e.g., +1 444-555-5450)
 *
 * @param phone - Phone number in any format
 * @param defaultRegion - Default region code (default: 'US')
 * @returns Phone number in international format
 *
 * @example
 * formatPhoneInternational('+14445555450') // Returns: +1 444-555-5450
 * formatPhoneInternational('4445555450', 'US') // Returns: +1 444-555-5450
 * formatPhoneInternational('+821012345678') // Returns: +82 10-1234-5678
 */
export function formatPhoneInternational(
  phone: string,
  defaultRegion: string = 'US'
): string {
  const lib = getPhoneUtilSync()
  if (!lib) {
    // Library not yet loaded, return as-is
    return phone
  }

  try {
    const number = lib.phoneUtil.parse(phone, defaultRegion)
    return lib.phoneUtil.format(number, lib.PhoneNumberFormat.INTERNATIONAL)
  } catch {
    return phone
  }
}

/**
 * Validate phone number (international support)
 *
 * @param phone - Phone number to validate
 * @param defaultRegion - Default region code (default: 'US')
 * @returns True if valid phone number
 *
 * @example
 * isValidPhone('+14445555450') // US: true
 * isValidPhone('4165551234', 'CA') // Canada: true
 * isValidPhone('+821012345678') // Korea: true
 * isValidPhone('123') // Returns: false
 */
export function isValidPhone(
  phone: string,
  defaultRegion: string = 'US'
): boolean {
  const lib = getPhoneUtilSync()
  if (!lib) {
    // Library not yet loaded, do basic length check
    const cleaned = phone.replace(/\D/g, '')
    return cleaned.length >= 10 && cleaned.length <= 15
  }

  try {
    const number = lib.phoneUtil.parse(phone, defaultRegion)
    return lib.phoneUtil.isValidNumber(number)
  } catch {
    return false
  }
}

/**
 * Async version of isValidPhone - use when you need guaranteed validation
 * Loads library if needed before validating
 */
export async function isValidPhoneAsync(
  phone: string,
  defaultRegion: string = 'US'
): Promise<boolean> {
  const lib = await getPhoneUtil()

  try {
    const number = lib.phoneUtil.parse(phone, defaultRegion)
    return lib.phoneUtil.isValidNumber(number)
  } catch {
    return false
  }
}

/**
 * Convert phone number to E.164 format
 * Used for API requests and database storage
 *
 * @param phone - Phone number in any format
 * @param defaultRegion - Default region code (default: 'US')
 * @returns Phone number in E.164 format (e.g., +14445555450) or null if invalid
 *
 * @example
 * toE164('(444) 555-5450', 'US') // Returns: +14445555450
 * toE164('4445555450', 'US') // Returns: +14445555450
 * toE164('010-1234-5678', 'KR') // Returns: +821012345678
 * toE164('123') // Returns: null
 */
export function toE164(
  phone: string,
  defaultRegion: string = 'US'
): string | null {
  const lib = getPhoneUtilSync()
  if (!lib) {
    // Library not yet loaded, do simple conversion for US numbers
    const cleaned = phone.replace(/\D/g, '')
    if (cleaned.length === 10) {
      return `+1${cleaned}`
    }
    if (cleaned.length === 11 && cleaned.startsWith('1')) {
      return `+${cleaned}`
    }
    return null
  }

  try {
    const number = lib.phoneUtil.parse(phone, defaultRegion)
    if (lib.phoneUtil.isValidNumber(number)) {
      return lib.phoneUtil.format(number, lib.PhoneNumberFormat.E164)
    }
    return null
  } catch {
    return null
  }
}

/**
 * Async version of toE164 - use when you need guaranteed conversion
 * Loads library if needed before converting
 */
export async function toE164Async(
  phone: string,
  defaultRegion: string = 'US'
): Promise<string | null> {
  const lib = await getPhoneUtil()

  try {
    const number = lib.phoneUtil.parse(phone, defaultRegion)
    if (lib.phoneUtil.isValidNumber(number)) {
      return lib.phoneUtil.format(number, lib.PhoneNumberFormat.E164)
    }
    return null
  } catch {
    return null
  }
}

/**
 * Get phone number country/region code
 *
 * @param phone - Phone number in E.164 format
 * @returns ISO country code (e.g., 'US', 'CA', 'KR', 'GB') or null if invalid
 *
 * @example
 * getPhoneCountry('+14445555450') // Returns: 'US'
 * getPhoneCountry('+14165551234') // Returns: 'CA'
 * getPhoneCountry('+821012345678') // Returns: 'KR'
 * getPhoneCountry('+442071234567') // Returns: 'GB'
 */
export function getPhoneCountry(phone: string): string | null {
  const lib = getPhoneUtilSync()
  if (!lib) {
    // Library not yet loaded, check for common prefixes
    if (phone.startsWith('+1')) return 'US'
    if (phone.startsWith('+82')) return 'KR'
    if (phone.startsWith('+44')) return 'GB'
    return null
  }

  try {
    const number = lib.phoneUtil.parse(phone)
    return lib.phoneUtil.getRegionCodeForNumber(number) || null
  } catch {
    return null
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// NANP E.164 helpers — aligned with the API contract
// API regex: /^\+1[2-9]\d{9}$/
// ─────────────────────────────────────────────────────────────────────────────

export const NANP_PHONE_INVALID_MESSAGE =
  'Please enter a valid US/Canada phone number'

/** Normalize friendly NANP input to the national digits used by PhoneInput. */
export function normalizeNANPNationalDigits(raw: string): string {
  const digits = raw.trim().replace(/\D/g, '')
  if (digits.length === 11 && digits.startsWith('1')) return digits.slice(1)
  return digits
}

/**
 * Convert a raw phone string to E.164 NANP format (+1XXXXXXXXXX).
 *
 * This is format normalization only. Use ensureE164NANP for the final
 * API/Telnyx boundary check.
 *
 * @example
 * toE164NANP('2125551234')    // '+12125551234'
 * toE164NANP('12125551234')   // '+12125551234'
 * toE164NANP('+12125551234')  // '+12125551234'
 * toE164NANP('+1 (212) 555-1234') // '+12125551234'
 * toE164NANP('447911123456')  // null  — non-US, not 10 or 11 digits starting 1
 * toE164NANP('0125551234')    // '+10125551234' — callers must validate area code
 */
export function toE164NANP(raw: string): string | null {
  const trimmed = raw.trim()
  const hasPlus = trimmed.startsWith('+')
  const digits = trimmed.replace(/\D/g, '')

  if (hasPlus) {
    return digits.length >= 10 ? `+${digits}` : null
  }
  if (digits.length === 10) return `+1${digits}`
  if (digits.length === 11 && digits.startsWith('1')) return `+${digits}`
  return null
}

/** Return canonical NANP E.164 only when the value matches the API contract. */
export function ensureE164NANP(raw: string): string | null {
  const normalized = toE164NANP(raw)
  if (!normalized || !isValidNANP(normalized)) return null
  return normalized
}

export function isValidNANPInput(raw: string): boolean {
  return ensureE164NANP(raw) !== null
}

export function getNANPPhoneValidationError(
  raw: string,
  options: {
    required?: boolean
    requiredMessage?: string
    invalidMessage?: string
  } = {}
): string | null {
  const {
    required = false,
    requiredMessage = 'Phone number is required',
    invalidMessage = NANP_PHONE_INVALID_MESSAGE,
  } = options

  if (!raw.trim()) return required ? requiredMessage : null
  return isValidNANPInput(raw) ? null : invalidMessage
}

/**
 * Regex that matches a valid NANP E.164 number.
 * Area code first digit must be 2-9 (0 and 1 are reserved).
 * This is what Telnyx enforces and what the API's final validator checks.
 */
export const NANP_E164_REGEX = /^\+1[2-9]\d{9}$/

/**
 * Return true only if the phone is a valid, routable NANP E.164 number.
 * Use this as the final client-side gate before sending to the API.
 */
export function isValidNANP(phone: string): boolean {
  return NANP_E164_REGEX.test(phone)
}
